import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from './../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { validators } from '../../../../_common/platform/ts/validators';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';

import { Constants } from '../../../../_common/platform/ts/Constants';

const Cookies = require('pdc-platform-cookies');

import { config } from '../config';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';
import { SettingsStore } from './SettingsStore';
import Fingerprint from './Fingerprint';
import { act } from 'react';

export interface IAuthState {
    loading: boolean;
    initialized: boolean;
    authorized: boolean;
    passwordReset: boolean;
    newUser: boolean;
    passwordResetEmail?: string;
    passwordResetToken?: string;
    errors: string[];
    displayName?: string;
    picture?: string;
    userToken?: string;
    userid?: number;
    homeFolderId?: number;
    passwordMinLength?: number;
    passwordMustHaveMixedLetterCase?: boolean;
    passwordMustContainLettresAndDigits?: boolean;
    checkEmail: boolean;
    approveNewDevice: boolean;
    approveNewDeviceToken?: string;
    openIDProviders?: {
        options?: {
            useLocalSignOn?: boolean;
            authSendFrom: 'frontend' | 'backend';
        };
        providers: IOpenIDProvider[];
    };
}

export interface IOpenIDProvider {
    name: string;
    description?: string;
    icon?: string;
    config: {
        auth_uri: string;
        token_uri: string;
        revoke_uri: string;
        client_secret: string;
        // required. See OpenID connect specification https://openid.net/specs/openid-connect-core-1_0.html
        scope: string;
        response_type: string;
        client_id: string;
        redirect_uri: string;
        // recommended
        state?: Object;
        // optional
        response_mode?: string;
        nonce?: string;
        display?: string;
        prompt?: string;
        max_age?: number;
        ui_locales?: string;
        id_token_hint?: string;
        login_hint?: string;
        acr_values?: string;
    };
}

interface ISingInResult {
    result?: {
        displayName?: string;
        picture?: string;
        userToken?: string;
        userid?: number;
        expiresIn?: number;
        message?: string;
        newDevice?: boolean;
    };

    error?: {
        message?: string;
    };
}

export class AuthStore extends BaseStore<IAuthState, AppActions, Dispatcher, IGlob> {
    public _state: IAuthState;
    public static _name = 'auth';

    public static getInitialState() {
        return {
            authorized: false,
            initialized: false,
            loading: false,
            passwordReset: false,
            newUser: false,
            errors: [],
            approveNewDevice: false,
            checkEmail: false
        };
    }

    public getErrors() {
        return this._state.errors;
    }

    public readCache() {}

    public getUserToken() {
        return this._state.userToken;
    }

    private overrideFail(def) {
        var _def = $.Deferred<any>();

        def.then(
            (...args) => {
                _def.resolve.apply(_def, args);
            },
            () => {
                _def.resolve();
            }
        );

        return _def;
    }

    // VR: it is needed to be PUBLIC here for JEST:
    public loadResourcesAfterAuth(resp: ISingInResult) {
        return $.when
            .call(
                $,
                this.glob.ThemeStore.themeGetList().then(() => {
                    return this.glob.SettingsStore.appSettingsGet([], SettingsStore.getInitialAppSettings());
                }),
                this.overrideFail(this.getHomeFolder()),
                this.overrideFail(this.glob.PermissionsStore.loadUserPermissionsDescription()),
                this.overrideFail(this.glob.PermissionsStore.loadUserPermissions(resp.result.userid))
            )
            .then(() => {
                return resp;
            });
    }

    private loadDefaultTheme() {
        $.when.call($, this.glob.SettingsStore.appSettingsGetDefaultTheme()).then(() => {});
    }

    private getHomeFolder() {
        return apiCall(this.actions, 'workspace_GetHomeDir', {}).done((resp) => {
            this._state.homeFolderId = resp.result as number;
        });
    }

    private onGetUserToken = (userToken, userDef) => {
        this._state.userToken = userToken;
        this._state.loading = true;

        if (userToken == null || userToken === '') {
            userDef.reject();
        } else {
            if (userToken.startsWith('authFailed:')) {
                this._state.errors = [userToken.substring('authFailed:'.length)];
                userDef.reject();
            } else {
                apiCall(this.actions, 'user_GetAccountInfo', {}, null, userDef);
            }
        }
    };

    private initialLoad(userToken_?: string) {
        this.loadDefaultTheme();

        const userDef = $.Deferred<any>();

        if (this._state.initialized === true) {
            return;
        }

        let userToken;

        if (userToken_ == null) {
            // For the same-origin requests, we get the Cookie at backed side

            if (this.sameOrigin()) {
                fetch(config.getAPIEndpoint() + '/auth', {
                    method: 'POST',
                    credentials: 'same-origin',

                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        method: 'auth_GetUserToken',
                        params: {},
                        id: 1
                    })
                })
                    .then(async (response) => {
                        if (!response.ok) {
                            let errorInfo;
                            try {
                                // Try to get error details from response body
                                errorInfo = await response.json();
                            } catch (e) {
                                // Fallback if response isn't JSON
                                errorInfo = response.statusText;
                            }
                            const error = new Error(`HTTP ${response.status} ${JSON.stringify(errorInfo)}`);
                            throw error;
                        } else {
                            return response.json();
                        }
                    })
                    .then((result) => {
                        userToken = result.result?.userToken;
                        if (this.signOutDef != null) {
                            userDef.reject();
                        } else {
                            this.onGetUserToken(userToken, userDef);
                        }
                    });
            } else {
                // For CORS requests we access cookes in a browse
                // It is a risk for XSS atacks
                userToken = Cookies.get(config.AUTH_COOKIE_NAME);
                this.onGetUserToken(userToken, userDef);
            }
        } else {
            // saved token still valid
            userToken = userToken_;
            this.onGetUserToken(userToken, userDef);
        }

        return userDef
            .then((resp) => {
                this._state.displayName = resp.result.displayName;

                return this.loadResourcesAfterAuth(resp);
            })
            .done((resp) => {
                this._state.authorized = true;
                this.actions.authFinish(
                    this._state.displayName,
                    resp.result.userid,
                    this._state.userToken,
                    resp.result.picture
                );
            })
            .fail((resp) => {
                this.unauthorized();
            })
            .always((resp) => {
                this._state.loading = false;
                this._state.initialized = true;

                this.actions.initialFinish(this._state.authorized);
            });
    }

    private authRefreshToken(userToken: string) {
        fetch(config.getAPIEndpoint() + '/auth', {
            method: 'POST',
            credentials: 'same-origin',

            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                method: 'auth_RefreshUserToken',
                params: { userToken: userToken },
                id: 1
            })
        })
            .then(async (response) => {
                if (!response.ok) {
                    let errorInfo;
                    try {
                        // Try to get error details from response body
                        errorInfo = await response.json();
                    } catch (e) {
                        // Fallback if response isn't JSON
                        errorInfo = response.statusText;
                    }
                    const error = new Error(`HTTP ${response.status} ${JSON.stringify(errorInfo)}`);
                    throw error;
                } else {
                    return response.json();
                }
            })
            .then((resp) => {
                // Do logout when token is expired (the refresh token method failed)
                console.log('refresh redirect ', JSON.stringify(resp));
                if (resp.result != null && resp.result.redirect != null) {
                    if (resp.result.redirect === './') {
                        this._state = AuthStore.getInitialState();

                        if (!this.sameOrigin()) {
                            Cookies.expire(config.AUTH_COOKIE_NAME, {
                                path: this.getPath()
                            });
                        }
                    } else {
                        window.location.href = resp.result.redirect;
                    }
                }
            });
    }

    public getOpenIDProviders(outerDef?) {
        const self = this;
        if (self._state.openIDProviders != null) {
            return;
        }
        const def = $.Deferred<any>();
        $.ajax({
            url: config.getAPIEndpoint(),
            crossDomain: true,
            success: () => {
                apiCall(
                    self.actions,
                    'auth_GetOpenIDProviders',
                    {},
                    {
                        endPoint: config.getAPIEndpoint() + '/auth'
                    },
                    def,
                    { suppressErrors: true }
                );
                def.then((resp) => {
                    self._state.openIDProviders = resp.result;
                    outerDef?.resolve();
                }).always(() => {
                    self.emitChange();
                });
            },
            error: function () {
                self._state.loading = false;
                self._state.errors = ['Network Error'];
                self.emitChange();
                outerDef?.reject();
            }
        });
    }

    private getPath() {
        if (config.AUTH_COOKIE_PATH != null) {
            return config.AUTH_COOKIE_PATH;
        } else {
            return location.pathname;
        }
    }

    private approveNewDevice(token: string) {
        const self = this;

        this._state.loading = true;

        const def = $.Deferred<any>();

        $.ajax({
            url: config.getAPIEndpoint(),
            crossDomain: true,
            success: () => {
                apiCall(
                    self.actions,
                    'auth_ApproveDevice',
                    {
                        fingerprint: Fingerprint.getFingerPrint(),
                        token: token
                    },
                    {
                        endPoint: config.getAPIEndpoint() + '/auth'
                    },
                    def,
                    { suppressErrors: true }
                );
                def.then((resp: ISingInResult) => {})
                    .done(() => {
                        self._state.approveNewDevice = false;
                        self._state.loading = false;
                    })
                    .fail((resp: ISingInResult) => {
                        let message;

                        if (typeof resp.error === 'string') {
                            message = resp.error;
                        } else if (resp.error != null) {
                            message = resp.error.message;
                        } else if (resp.result != null) {
                            message = resp.result.message;
                        }
                        if (message != null) {
                            self._state.errors = [message];
                        }
                        self._state.approveNewDevice = false;
                    })
                    .always(() => {
                        self._state.loading = false;
                        self.emitChange();
                    });
            },
            error: function () {
                self._state.loading = false;
                self._state.errors = ['Network Error'];
                self.emitChange();
            }
        });
    }

    private sameOrigin() {
        return config.getAPIEndpoint() === '.';
    }

    private signIn(name, password, rememberMe) {
        const self = this;
        if (name === '' || password === '') {
            this._state.errors = ['Username and password should be specified.'];
        } else {
            this._state.loading = true;
            const def = $.Deferred<any>();
            // Do not ise geoLocation for a while
            // See conversation with Leo from 06.08.2024
            Fingerprint.getBackendInfo(false).then((backendInfo) => {
                $.ajax({
                    url: config.getAPIEndpoint(),
                    crossDomain: true,
                    success: () => {
                        apiCall(
                            self.actions,
                            'auth_Login',
                            {
                                login: name,
                                password: password,
                                fingerprint: Fingerprint.getFingerPrint(),
                                internal: Fingerprint.isInternal(),
                                devname: backendInfo,
                                sameOrigin: this.sameOrigin()
                            },
                            {
                                endPoint: config.getAPIEndpoint() + '/auth'
                            },
                            def,
                            { suppressErrors: true }
                        );

                        def.then((resp: ISingInResult) => {
                            if (resp.result.newDevice === true) {
                                self._state.loading = false;
                                self._state.errors = [
                                    'We noticed new sign-in to your account on a ' +
                                        Fingerprint.currDeviceName() +
                                        '. Check your email to approve new device.'
                                ];
                                self.emitChange();
                            } else {
                                self._state.displayName = resp.result.displayName;
                                self._state.picture = resp.result.picture;
                                self._state.userToken = resp.result.userToken;
                                self._state.userid = resp.result.userid;
                                self._state.errors = [];
                                var options: any = {};

                                options.path = self.getPath();

                                if (rememberMe === true) {
                                    options.expires = resp.result.expiresIn || config.AUTH_COOKIE_DEFAULT_EXPIRES;
                                }

                                if (location.protocol.toLowerCase().startsWith('https')) {
                                    options.secure = true;
                                }

                                if (!this.sameOrigin()) {
                                    Cookies.set(config.AUTH_COOKIE_NAME, resp.result.userToken, options);
                                }

                                return self.loadResourcesAfterAuth(resp);
                            }
                        })
                            .done((resp: ISingInResult) => {
                                if (resp.result.newDevice !== true) {
                                    self._state.loading = false;
                                    self._state.authorized = true;

                                    self.actions.authFinish(
                                        self._state.displayName,
                                        self._state.userid,
                                        self._state.userToken,
                                        self._state.picture
                                    );
                                }
                            })
                            .fail((resp: ISingInResult) => {
                                let message;

                                if (typeof resp.error === 'string') {
                                    message = resp.error;
                                } else if (resp.error != null) {
                                    message = resp.error.message;
                                } else if (resp.result != null) {
                                    message = resp.result.message;
                                }

                                if (message != null) {
                                    self._state.errors = [message];
                                }
                            })
                            .always(() => {
                                self._state.loading = false;

                                self.emitChange();
                            });
                    },
                    error: function () {
                        self._state.loading = false;
                        self._state.errors = ['Network Error'];
                        self.emitChange();
                    }
                });
            });
        }
    }

    private signInWithOpenID(providerName: string, queryString?: string) {
        const provider: IOpenIDProvider = this._state.openIDProviders.providers.find(
            (p) => p.name.toLowerCase() === providerName.toLowerCase()
        );
        if (provider != null) {
            const a = window.location;
            let pathname;
            if (a.pathname.endsWith('/')) {
                pathname = a.pathname.substring(0, a.pathname.length - 1);
            } else {
                pathname = a.pathname;
            }

            let state = provider.config.state != null ? provider.config.state : {};
            state['origurl'] = window.location.href;

            const QueryString =
                'client_id=' +
                provider.config.client_id +
                '&response_type=' +
                provider.config.response_type +
                '&scope=' +
                provider.config.scope +
                (provider.config.acr_values != null ? '&acr_values=' + provider.config.acr_values : '') +
                (provider.config.prompt != null ? '&prompt=' + provider.config.prompt : '') +
                '&redirect_uri=' +
                a.protocol +
                '//' +
                a.host +
                pathname +
                (provider.config.redirect_uri.startsWith('/')
                    ? provider.config.redirect_uri
                    : '/' + provider.config.redirect_uri) +
                '&nonce=' +
                new Date().valueOf() +
                '&state=' +
                btoa(JSON.stringify(state));

            const Location = provider.config.auth_uri + '?' + encodeURI(QueryString);
            console.log('Call OpenID:', Location);
            window.location.replace(Location);
        }
    }

    private signOutDef = null;
    protected signOut() {
        this.signOutDef = $.Deferred<{ result: any }>();

        apiCall(
            this.actions,
            'auth_Logout',
            {
                token: this.getUserToken()
            },
            {
                endPoint: config.getAPIEndpoint() + '/auth'
            },
            this.signOutDef
        );

        this.signOutDef.then((resp) => {
            if (resp.result != null && resp.result.redirect != null && resp.result.redirect !== './') {
                window.location.href = resp.result.redirect;
            } else {
                this._state = AuthStore.getInitialState();

                if (!this.sameOrigin()) {
                    Cookies.expire(config.AUTH_COOKIE_NAME, {
                        path: this.getPath()
                    });
                }
            }
            this.signOutDef = null;
        });
    }

    private recoverPasswordRequest(email) {
        const self = this;
        const def = $.Deferred<any>();

        this._state.errors = [];

        if (email === '' || email == null) {
            this._state.errors = ['Email should be specified.'];
            return;
        }

        const validation = validators.string.email(email);

        if (validation != null) {
            this._state.errors = validation;
            return;
        }

        this._state.loading = true;

        $.ajax({
            url: config.getAPIEndpoint(),
            crossDomain: true,
            success: () => {
                apiCall(
                    self.actions,
                    'auth_RecoverPassword',
                    {
                        Email: email
                    },
                    {
                        endPoint: config.getAPIEndpoint() + '/auth'
                    },
                    def
                );

                def.done((resp) => {
                    /*self.actions.showNotification_Success(
                        "A password recovery was successfully requested for email '" + email + "'",
                        null
                    );*/
                });

                def.fail((resp) => {
                    var message;

                    if (typeof resp.error === 'string') {
                        message = resp.error;
                    } else {
                        message = resp.error && resp.error.message;
                    }

                    self._state.errors = [message];
                });

                def.always((resp) => {
                    self.actions.recoverPasswordRequestFinish(email);
                });
            },
            error: function () {
                self._state.loading = false;
                self._state.errors = ['Network Error'];
                self.emitChange();
            }
        });
    }

    private recoverPasswordRequestFinish(email?) {
        this._state.loading = false;
        this._state.checkEmail = true;
    }

    private recoverPasswordRequestReset() {
        this._state.checkEmail = false;
    }

    private passwordResetStart(email, token, isNew) {
        if (isNew === true) {
            this._state.newUser = true;
            this._state.passwordReset = false;
        } else {
            this._state.newUser = false;
            this._state.passwordReset = true;
        }

        this._state.passwordResetEmail = email;
        this._state.passwordResetToken = token;
    }

    private approveNewDeviceStart(token) {
        this._state.errors = [];
        this._state.approveNewDevice = true;
        this._state.approveNewDeviceToken = token;
    }

    private passwordResetEnd() {
        this._state.passwordReset = false;
    }

    private validatePassword(oldPassword: string, password: string, confirmPassword: string) {
        let result = validators.password.oldPasswordNotEmpty(oldPassword);

        if (result == null) {
            result = validators.password.notEmpty(password);
        }

        if (result == null) {
            result = validators.password.length(this._state.passwordMinLength)(password);
        }

        if (result == null && this._state.passwordMustContainLettresAndDigits) {
            result = validators.password.containsNumbersAndLetters(password);
        }

        if (result == null && this._state.passwordMustHaveMixedLetterCase) {
            result = validators.password.mixedLetterCase(password);
        }

        if (result == null) {
            result = validators.password.shouldBeConfirmed(password, confirmPassword);
        }

        return result;
    }

    private passwordResetRequest(email: string, token, password: string, confirmPassword: string) {
        this._state.errors = [];

        if (email === '' || email == null) {
            this._state.errors = ['Email should be specified.'];
            return;
        }

        if (token === '' || token == null) {
            this._state.errors = ['Token should be specified.'];
            return;
        }

        const res = this.validatePassword('XXX', password, confirmPassword);

        if (res != null) {
            this._state.errors = res;
            return;
        }

        this._state.loading = true;

        const method = this._state.newUser === true ? 'auth_SetPasswordNewUser' : 'auth_ResetPassword';

        apiCall(
            this.actions,
            method,
            {
                Email: email,
                Token: token,
                NewPassword: password
            },
            {
                endPoint: config.getAPIEndpoint() + '/auth'
            }
        )
            .done((resp) => {
                this.actions.showNotification_Success(`A password was successfully reset for email '${email}'`);

                this.actions.signOut();
                this.actions.signIn(email, password, true);
            })
            .fail((resp) => {
                let message;

                if (typeof resp.error === 'string') {
                    message = resp.error;
                } else {
                    message = resp.error && resp.error.message;
                }

                this._state.errors = [message];
            })
            .always((resp) => {
                this.actions.passwordResetRequestFinish(email);
            });
    }

    private passwordResetRequestFinish(email?) {
        this._state.loading = false;
    }

    private changePassword(userID: number, oldPassword: string, newPassword: string, confirmPassword: string) {
        const res = this.validatePassword(oldPassword, newPassword, confirmPassword);

        if (res != null) {
            this._state.errors = res;
            return;
        }

        this._state.errors = [];
        this._state.loading = true;

        this.glob.AQLQueries.callMethod('user_ChangePassword', {
            userid: userID,
            oldPassword: oldPassword,
            newPassword: newPassword
        })
            .done((resp) => {
                this.glob.AppActions.showNotification_Success('Password has been changed successfully.');
            })
            .fail((resp) => {
                //this.glob.AppActions.showNotification_Fail('Fail to change password.');
                let message;

                if (typeof resp.error === 'string') {
                    message = resp.error;
                } else {
                    message = resp.error && resp.error.message;
                }

                this._state.errors = [message];
            })
            .always(() => {
                this.actions.changePasswordFinish();
            });
    }

    private changePasswordFinish() {
        this._state.loading = false;
    }

    private passwordGetRequirements() {
        apiCall(
            this.actions,
            'auth_GetPasswordRequirements',
            {},
            {
                endPoint: config.getAPIEndpoint() + '/auth'
            },
            null,
            {
                suppressErrors: true
            }
        ).done((response: { result: any }) => {
            this.actions.passwordGetRequirementsFinish(
                response.result.minLength,
                response.result.mustHaveMixedLetterCase,
                response.result.mustContainLettersAndDigits
            );
        });
    }

    // public getPasswrdRequirements() {
    //     return {
    //         minLength: this._state.passwordMinLength,
    //         mustContainLettresAndDigits: this._state.passwordMustContainLettresAndDigits,
    //         mustHaveMixedLetterCase: this._state.passwordMustHaveMixedLetterCase
    //     };
    // }

    private passwordGetRequirementsFinish(
        minLength: number,
        mustHaveMixedLetterCase: boolean,
        mustContainLetterAndDigits: boolean
    ) {
        this._state.passwordMinLength = minLength;
        this._state.passwordMustContainLettresAndDigits = mustContainLetterAndDigits;
        this._state.passwordMustHaveMixedLetterCase = mustHaveMixedLetterCase;
    }

    private unauthorized() {
        this._state.authorized = false;
        this._state.userToken = null;

        if (this.sameOrigin()) {
            apiCall(
                this.actions,
                'auth_Unauthorized',
                {},
                {
                    endPoint: config.getAPIEndpoint() + '/auth'
                },
                null,
                {
                    suppressErrors: true
                }
            );
        } else {
            Cookies.expire(config.AUTH_COOKIE_NAME, {
                path: this.getPath()
            });
        }
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const action = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.INITIAL_LOAD>(action, Constants.INITIAL_LOAD)) {
                this.initialLoad(action.userToken);
                this.emitChange();
                break;
            }

            if (_case<I.SIGN_IN>(action, Constants.SIGN_IN)) {
                this.signIn(action.name, action.password, action.rememberMe);
                this.emitChange();
                break;
            }

            if (_case<I.SIGN_IN_WITH_OPENID>(action, Constants.SIGN_IN_WITH_OPENID)) {
                this.signInWithOpenID(action.providerName, action.queryString);
                this.emitChange();
                break;
            }

            if (_case<I.APPROVE_NEW_DEVICE>(action, Constants.APPROVE_NEW_DEVICE)) {
                this.approveNewDevice(action.token);
                this.emitChange();
                break;
            }
            if (_case<I.RECOVER_PASSWORD_REQUEST>(action, Constants.RECOVER_PASSWORD_REQUEST)) {
                this.recoverPasswordRequest(action.email);
                this.emitChange();
                break;
            }

            if (_case<I.RECOVER_PASSWORD_REQUEST_FINISH>(action, Constants.RECOVER_PASSWORD_REQUEST_FINISH)) {
                this.recoverPasswordRequestFinish(action.email);
                this.emitChange();
                break;
            }

            if (_case<I.RECOVER_PASSWORD_REQUEST_RESET>(action, Constants.RECOVER_PASSWORD_REQUEST_RESET)) {
                this.recoverPasswordRequestReset();
                this.emitChange();
                break;
            }

            if (_case<I.CHANGE_PASSWORD>(action, Constants.CHANGE_PASSWORD)) {
                this.changePassword(action.userID, action.oldPassword, action.newPassword, action.confirmPassword);
                this.emitChange();
                break;
            }

            if (_case<I.CHANGE_PASSWORD_FINISH>(action, Constants.CHANGE_PASSWORD_FINISH)) {
                this.changePasswordFinish();
                this.emitChange();
                break;
            }

            if (_case<I.PASSWORD_RESET_START>(action, Constants.PASSWORD_RESET_START)) {
                this.passwordResetStart(action.email, action.token, action.isNew);
                this.emitChange();
                break;
            }

            if (_case<I.APPROVE_NEW_DEVICE_START>(action, Constants.APPROVE_NEW_DEVICE_START)) {
                this.approveNewDeviceStart(action.token);
                this.emitChange();
                break;
            }

            if (_case<I.PASSWORD_RESET_END>(action, Constants.PASSWORD_RESET_END)) {
                this.passwordResetEnd();
                this.emitChange();
                break;
            }

            if (_case<I.PASSWORD_RESET_REQUEST>(action, Constants.PASSWORD_RESET_REQUEST)) {
                this.passwordResetRequest(action.email, action.token, action.password, action.passwordRepeat);
                this.emitChange();
                break;
            }

            if (_case<I.PASSWORD_RESET_REQUEST_FINISH>(action, Constants.PASSWORD_RESET_REQUEST_FINISH)) {
                this.passwordResetRequestFinish(action.email);
                this.emitChange();
                break;
            }

            if (_case<I.PASSWORD_GET_REQUIREMENTS>(action, Constants.PASSWORD_GET_REQUIREMENTS)) {
                this.passwordGetRequirements();
                this.emitChange();
                break;
            }

            if (_case<I.PASSWORD_GET_REQUIREMENTS_FINISH>(action, Constants.PASSWORD_GET_REQUIREMENTS_FINISH)) {
                this.passwordGetRequirementsFinish(
                    action.minLength,
                    action.mustHaveMixedLetterCase,
                    action.mustContainLetterAndDigits
                );
                this.emitChange();
                break;
            }

            if (_case<I.UNAUTHORIZED>(action, Constants.UNAUTHORIZED)) {
                this.unauthorized();
                this.emitChange();
                break;
            }

            if (_case<I.AUTH_REFRESH_TOKEN>(action, Constants.AUTH_REFRESH_TOKEN)) {
                this.authRefreshToken(action.userToken);
                this.emitChange();
                break;
            }
        } while (false);

        return true;
    }

    constructor(glob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, AuthStore._name);

        this._state = _.extend(AuthStore.getInitialState(), this._state);
        this.getOpenIDProviders();
    }

    static get(glob, dispatcher: Dispatcher, actions: AppActions) {
        return new AuthStore(glob, dispatcher, actions);
    }
}
